import { cn } from "@/lib/utils";
import type { EvadibilitaStato, LineaStato } from "@/types/m2";
import { coloreStampo, saturazioneColor } from "@/pages/M2/format";

export function StampoChip({ stampo }: { stampo: string | null }) {
  if (!stampo) return <span className="text-xs text-muted-foreground">—</span>;
  const c = coloreStampo(stampo);
  return (
    <span
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[11px] font-medium"
      style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}
    >
      <span className="size-2 rounded-sm" style={{ background: c.border }} />
      {stampo}
    </span>
  );
}

// ── Tag del tipo di setup tra due lavorazioni (doc #2: cambio stampo) ──
const SETUP_META: Record<string, { label: string; cls: string }> = {
  cambio_stampo: { label: "cambio stampo", cls: "bg-red-100 text-red-800" },
  cambio_ricetta: { label: "cambio ricetta", cls: "bg-secondary text-muted-foreground" },
  bonifica_allergeni: { label: "bonifica", cls: "bg-amber-100 text-amber-800" },
};

export function SetupTag({ tipo, minuti }: { tipo: string | null; minuti: number | null }) {
  if (!tipo) return <span className="text-xs text-muted-foreground">—</span>;
  const meta = SETUP_META[tipo] ?? { label: tipo, cls: "bg-secondary text-muted-foreground" };
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-medium", meta.cls)}>
      {meta.label} {minuti ? `${minuti}'` : ""}
    </span>
  );
}

// ── Badge stato evadibilità (disponibilità / contratto M2↔M3) ────────
const EVAD_META: Record<EvadibilitaStato, { label: string; cls: string }> = {
  puntuale: { label: "Puntuale", cls: "bg-emerald-50 text-emerald-700" },
  in_ritardo: { label: "In ritardo", cls: "bg-red-100 text-red-800" },
  a_rischio: { label: "A rischio", cls: "bg-amber-100 text-amber-800" },
  inevadibile: { label: "Inevadibile", cls: "bg-slate-200 text-slate-600" },
};

export function EvadibilitaBadge({ stato }: { stato: EvadibilitaStato }) {
  const m = EVAD_META[stato];
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", m.cls)}>{m.label}</span>
  );
}

// ── Barra di saturazione linea (verde / ambra / rosso = sovraccarico) ──
export function SaturazioneBar({ pct, className }: { pct: number | null; className?: string }) {
  if (pct === null) return <span className="text-xs text-muted-foreground">n/d</span>;
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="h-1.5 w-[90px] overflow-hidden rounded-full bg-slate-200">
        <div
          className={cn("h-full rounded-full", saturazioneColor(pct))}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <span className="tabular-nums text-xs text-muted-foreground">{pct}%</span>
    </div>
  );
}

// ── Badge stato rilevato della linea ─────────────────────────────────
const STATO_META: Record<LineaStato, { label: string; cls: string }> = {
  principale: { label: "Principale", cls: "bg-accent text-accent-foreground" },
  attiva: { label: "Attiva", cls: "bg-secondary text-muted-foreground" },
  secondaria: { label: "Secondaria", cls: "bg-secondary text-muted-foreground" },
  marginale: { label: "Marginale", cls: "bg-amber-50 text-amber-700" },
  da_verificare: { label: "Da verificare", cls: "bg-amber-100 text-amber-800" },
};

export function StatoLineaBadge({ stato }: { stato: LineaStato | null }) {
  if (!stato) return null;
  const m = STATO_META[stato];
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", m.cls)}>{m.label}</span>
  );
}

// ── Barra di copertura ───────────────────────────────────────────────
export function CoverageBar({ titolo, pct, nota }: { titolo: string; pct: number; nota: string }) {
  return (
    <div className="min-w-[130px] flex-1">
      <div className="mb-1 text-xs text-muted-foreground">{titolo}</div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-200">
        <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="mt-1 text-xs font-medium">{nota}</div>
    </div>
  );
}
